import { CdkDrag, DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
    afterNextRender,
    Component,
    computed,
    effect,
    ElementRef,
    HostListener,
    inject,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    signal,
    untracked,
    ViewChild,
    viewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';

import {
    AGENT_FEATURE,
    GUIDED_WORKFLOWS_FEATURE,
} from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import { GUIDED_WORKFLOWS_OPEN_SUBCATEGORY_COOKIE_NAME } from 'QuickflowsApp/ts/constants';
import { SECURITY_ID_REGEX_SOURCE } from 'SecuritiesApp/ts/constants/security-ids.constants';
import { ToastMessageType } from 'Angular/features/components/toast-messages/toast-messages.enum';
import { AngularUser } from 'Angular/interfaces/angular-user.interface';
// Types / Interfaces / Enums
import {
    GuidedWorkflowSubcategory,
    PageGuidedWorkflows,
} from 'GuidedWorkflowsApp/ts/interfaces/guided-workflows.response';
import {
    BackgroundJob,
    BackgroundJobsInProgressResponse,
} from 'NotificationsApp/ts/interfaces/background-job.interface';
import { Notification } from 'NotificationsApp/ts/interfaces/notifications.interface';

import { AccountsService } from 'AccountsApp/ts/services/accounts.service';
import { AgentSidePanelStateService } from 'AgentApp/ts/services/agent-side-panel-state.service';
import { AgentUserPreferencesService } from 'AgentApp/ts/services/agent-user-preferences.service';
import { SideMenuToastMessagesService } from 'Angular/features/components/toast-messages/side-menu-toast-messages-service';
import { UrlsService } from 'Angular/services/core/urls.service';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';
import { GuidedWorkflowsService } from 'GuidedWorkflowsApp/ts/services/guided-workflows.service';
import { CookieService } from 'ngx-cookie-service';
import { BackgroundJobService } from 'NotificationsApp/ts/services/background-job.service';
import { BackgroundJobEventsService } from 'NotificationsApp/ts/services/background-job-events.service';
import { NotificationsService } from 'NotificationsApp/ts/services/notifications.service';

// Template dependencies
import { IconModule } from 'Angular/features/directives/icon/icon.module';
import { TooltipModule } from 'Angular/features/directives/popovers/tooltip/tooltip.module';
import { GuidedWorkflowsMenuModule } from 'GuidedWorkflowsApp/ts/features/guided-workflows-menu/guided-workflows-menu.module';
import { BackgroundJobsProgressPopoverModule } from 'NotificationsApp/ts/features/background-jobs-progress-popover/background-jobs-progress-popover.module';
import { NotificationsSideMenuModule } from 'NotificationsApp/ts/features/notifications-side-menu/notifications-side-menu.module';
import { QuickflowsMenuModule } from 'QuickflowsApp/ts/features/quickflows-menu/quickflows-menu.module';

import { AgentSidePanelComponent } from 'AgentApp/ts/features/agent/components/agent-side-panel/agent-side-panel.component';
import { SideMenuToastMessagesComponent } from 'Angular/features/components/toast-messages/side-menu-toast-messages.component';
// Components
import { GuidedWorkflowsMenuComponent } from 'GuidedWorkflowsApp/ts/features/guided-workflows-menu/components/guided-workflows-menu.component';
import { BackgroundJobsProgressPopoverComponent } from 'NotificationsApp/ts/features/background-jobs-progress-popover/components/background-jobs-progress-popover/background-jobs-progress-popover.component';
import { NotificationsSideMenuComponent } from 'NotificationsApp/ts/features/notifications-side-menu/components/notifications-side-menu/notifications-side-menu.component';

/**
 *
 * Side Menu Component
 *
 * Hosts app side tabs QuickflowsMenuComponent, GuidedWorkflowsMenuComponent, and AgentSidePanelComponent.
 * Quickflows component inputs:
 *   displaySecurityId: The display security ID to initialize this component with, e.g. VTSAX
 *   securityId: The security ID to initialize this component with, e.g. M:VTSAX
 *   securityName: The security name to initialize this component with, e.g. Vanguard Financials Index Admiral
 *   securityUrl: The security url to navigate e.g.  /mutual_funds/M:VFAIX
 *
 * Workflows component inputs:
 *  pagePath: the page path url to get categories
 *
 */

type SideMenuType = 'quickflows' | 'workflows' | 'ai_agent' | 'notifications';

@Component({
    selector: 'ycn-side-menu',
    templateUrl: './side-menu.template.html',
    imports: [
        AgentSidePanelComponent,
        BackgroundJobsProgressPopoverModule,
        CommonModule,
        DragDropModule,
        GuidedWorkflowsMenuModule,
        IconModule,
        NotificationsSideMenuModule,
        QuickflowsMenuModule,
        SideMenuToastMessagesComponent,
        TooltipModule,
    ],
    styles: [
        `
            :host {
                display: contents;
            }

            .detached {
                position: fixed;
                top: auto;
                right: auto;
                height: 80vh;
                border-radius: 8px;
                box-shadow: 0 8px 24px 0 rgba(0, 0, 0, 0.3);
                z-index: 1032;
                overflow: hidden;

                .side-panel-wrap {
                    max-height: 100%;
                    border-radius: 8px;
                }
            }
        `,
    ],
})
export class SideMenuComponent implements OnInit, OnDestroy {
    private readonly _agentSidepanelStateService = inject(AgentSidePanelStateService);

    private readonly _agentUserPreferencesService = inject(AgentUserPreferencesService);

    @HostListener('window:scroll', ['$event']) onScrollEvent(): void {
        const sidePanel = this.eltRef.nativeElement.querySelector('.side-panel.open');

        if (sidePanel && window.getComputedStyle(sidePanel).position === 'fixed') {
            this.sidePanelTop = Math.max(0, this.headerHeight - window.scrollY);
        } else {
            this.sidePanelTop = undefined;
        }
        this.sidePanelHeight =
            window.scrollY < this.headerHeight ? `calc(100vh - ${this.headerHeight - window.scrollY}px)` : '100vh';
    }

    // Quickflows
    @Input() displaySecurityId?: string;

    @Input() securityId?: string;

    @Input() securityName?: string;

    @Input() securityUrl?: string;

    @Input() showQuickflows: boolean = true;

    @Input() showNotifications: boolean = true;

    // Guided Workflows
    @Input() pagePath?: string;

    @ViewChild(GuidedWorkflowsMenuComponent) workflowsMenuComponent!: GuidedWorkflowsMenuComponent;

    @ViewChild(NotificationsSideMenuComponent) notificationsSideMenuComponent!: NotificationsSideMenuComponent;

    @ViewChild(BackgroundJobsProgressPopoverComponent)
    backgroundJobsProgressPopover!: BackgroundJobsProgressPopoverComponent;

    protected readonly cdkDrag = viewChild<CdkDrag>(CdkDrag);

    readonly isSidePanelOpen = signal<boolean>(false);

    readonly prefsLoaded = signal<boolean>(false);

    isMenuOpen: boolean = true;

    readonly type = signal<SideMenuType>('quickflows');

    userPreferencesUrl: string = '';

    showWorkflows: boolean = false;

    categories: PageGuidedWorkflows[] = [];

    readonly notifications = signal<Notification[]>([]);

    readonly unreadNotificationsCount = computed(() => this.notifications().filter((n) => !n.read).length);

    selectedSubcategoryId: number | null = null;

    sidePanelTop: number | undefined;

    sidePanelHeight: string | undefined;

    headerHeight: number = 48;

    readonly notificationsTopSectionPercentage = signal<number>(65);

    private notificationsFetchSubscription: Subscription | null = null;

    readonly showJobCompletedCheck = signal<boolean>(false);

    private jobCompletedCheckTimeout: ReturnType<typeof setTimeout> | null = null;

    alwaysShowJobsProgressPopover: boolean = true;

    private readonly MODEL_PORTFOLIO_QUICK_EXTRACT_JOB_TYPE = 'model_portfolio_quick_extract';

    private readonly _isPanelDetached = signal(false);

    protected readonly isDetached = computed(() => {
        const type = this.type();
        const isDetached = this._isPanelDetached();

        return type === 'ai_agent' && isDetached;
    });

    protected readonly savedDragPosition = signal<{ x: number; y: number }>({ x: 0, y: 0 });

    protected detachedPanelWidth = 586;

    protected readonly USER = signal<AngularUser>(window.USER);

    protected readonly agentPageUrl = computed(() => {
        const agentSidePanelSessionId = this._agentSidepanelStateService.currentSessionId();
        return `/agent/#/${agentSidePanelSessionId ? `?agentSessionId=${agentSidePanelSessionId}` : ''}`;
    });

    protected readonly showAgentButton = computed(() => {
        const isOnAgentPage = this.isOnAgentPage();
        return !isOnAgentPage;
    });

    protected readonly isNotificationsActive = computed(() => {
        const isSidePanelOpen = this.isSidePanelOpen();
        const type = this.type();
        return isSidePanelOpen && type === 'notifications';
    });

    protected readonly isAIAgentActive = computed(() => {
        const isSidePanelOpen = this.isSidePanelOpen();
        const type = this.type();
        return isSidePanelOpen && type === 'ai_agent';
    });

    protected readonly isQuickflowsActive = computed(() => {
        const isSidePanelOpen = this.isSidePanelOpen();
        const type = this.type();
        return isSidePanelOpen && type === 'quickflows';
    });

    protected readonly isWorkflowsActive = computed(() => {
        const isSidePanelOpen = this.isSidePanelOpen();
        const type = this.type();
        return isSidePanelOpen && type === 'workflows';
    });

    protected readonly isOnAgentPage = signal(false);

    protected readonly userHasAgentFeature = this.USER().features.includes(AGENT_FEATURE);

    constructor(
        private eltRef: ElementRef,
        private injector: Injector,
        private urlsService: UrlsService,
        private featureService: FeatureService,
        private cookieService: CookieService,
        private guidedWorkflowsService: GuidedWorkflowsService,
        private notificationsService: NotificationsService,
        private backgroundJobEventsService: BackgroundJobEventsService,
        private backgroundJobService: BackgroundJobService,
        private accountsService: AccountsService,
        private toastMessagesService: SideMenuToastMessagesService
    ) {
        this.isOnAgentPage.set(window.location?.pathname?.startsWith('/agent') ?? false);
        this.displaySecurityId = this.eltRef.nativeElement.getAttribute('displaySecurityId');
        this.securityId = this.eltRef.nativeElement.getAttribute('securityId');
        this.securityName = this.eltRef.nativeElement.getAttribute('securityName');
        this.securityUrl = this.eltRef.nativeElement.getAttribute('securityUrl');
        this.showQuickflows = JSON.parse(this.eltRef.nativeElement.getAttribute('showQuickflows')) ?? true;
        this.showNotifications = JSON.parse(this.eltRef.nativeElement.getAttribute('showNotifications')) ?? true;
        this.pagePath = this.eltRef.nativeElement.getAttribute('pagePath');
        this.accountsService.getUserPreferences().subscribe((preference) => {
            this.notificationsTopSectionPercentage.set(
                Math.min(85, Math.max(15, preference.side_menu_top_section_percentage))
            );
            this.alwaysShowJobsProgressPopover = preference.always_display_jobs_progress_popover;
            if (this.USER().authenticated && this.showNotifications) {
                // Only listen for running jobs after determining if we should open the popover
                // to avoid opening it prematurely.
                this.listenForRunningJobs();
            }

            if (
                preference.agent_side_panel_open_on_load &&
                this.USER().features.includes('agent') &&
                !this.isOnAgentPage() &&
                !this.hasGuidedWorkflowQueryParam()
            ) {
                this.type.set('ai_agent');
                this.isSidePanelOpen.set(true);
                afterNextRender(() => this.onScrollEvent(), { injector: this.injector });
            }

            this.prefsLoaded.set(true);
        });

        effect(() => {
            const cdkDrag = this.cdkDrag();
            const isDetached = this.isDetached();
            if (cdkDrag && isDetached) {
                const position = untracked(() => this.savedDragPosition());
                position.x = position.x || window.innerWidth - this.detachedPanelWidth - 100;
                cdkDrag.setFreeDragPosition(position);
            }
        });

        effect(() => {
            const hasRunningJobs = this.hasRunningJobs();
            const hasRunningQuickExtractJob = this.hasRunningQuickExtractJob();
            const popover = this.backgroundJobsProgressPopover;
            const editorWatchedJobId = this.backgroundJobEventsService.editorWatchedJobId();
            const runningJobIds = Object.keys(this.runningJobs());
            const isEditorWatchedJobRunning =
                editorWatchedJobId !== null && runningJobIds.length === 1 && editorWatchedJobId in this.runningJobs();

            if (popover?.isOpen && (!hasRunningJobs || isEditorWatchedJobRunning)) {
                // Close the running jobs popover if no more jobs are running, or if the only running job
                // is one an editor page is already surfacing progress for in its own in-editor loading state.
                popover.closeModal();
            }

            if (hasRunningJobs && !isEditorWatchedJobRunning && popover && !popover.isOpen) {
                if (this.alwaysShowJobsProgressPopover) {
                    // Open the running jobs popover if a job starts running.
                    // TODO: Add check to verify the user doesn't have the `show_jobs_progress_popover` set to false.
                    this.openBackgroundJobsProgressPopover();
                } else if (hasRunningQuickExtractJob) {
                    // Quick extract uploads always surface the progress popover so the running extraction
                    // is visible, even when the user has dismissed it for other job types. Open it directly
                    // without flipping their global `always_display_jobs_progress_popover` preference.
                    popover.openModal();
                }
            }
        });

        effect(() => {
            const terminalJob = this.backgroundJobEventsService.lastTerminalJob();
            if (terminalJob && terminalJob.status === 'completed') {
                this.fetchNotifications();
                const isWatchedInEditor = untracked(
                    () => this.backgroundJobEventsService.editorWatchedJobId() === terminalJob.job_id
                );
                if (isWatchedInEditor) {
                    // The editor page showing this job's in-editor Quick Extract loading state surfaces
                    // completion itself — skip the toast/auto-opened panel to avoid duplicating it.
                    return;
                }
                this.toastMessagesService.addMessage(
                    '<span class="text-bold">Task Complete</span>',
                    ToastMessageType.SUCCESS,
                    5000,
                    'icon-check'
                );
                this.flashJobCompletedCheck();
                // A completed Quick Extract opens the Notifications panel to the To Do tab so the
                // ready-to-validate task is immediately visible. untracked() keeps the panel signals out of
                // this effect's dependencies — otherwise opening the panel would re-fire it for the same job.
                untracked(() => {
                    const isQuickExtract = terminalJob.job_type === this.MODEL_PORTFOLIO_QUICK_EXTRACT_JOB_TYPE;
                    if (!isQuickExtract || !this.showNotifications) {
                        return;
                    }
                    if (!this.isSidePanelOpen() || this.type() !== 'notifications') {
                        this.togglePanel('notifications');
                    }
                    // Defer so the notifications component has mounted when the panel has just opened.
                    setTimeout(() => this.notificationsSideMenuComponent?.selectTab('todo'), 0);
                });
            }
        });
    }

    ngOnInit(): void {
        this.userPreferencesUrl = this.urlsService.ACCOUNTS.QUICKFLOWS;
        const hasGuidedWorkflowsFeature = this.featureService.userHasFeatures([GUIDED_WORKFLOWS_FEATURE]);
        const subcategoryCookie = this.cookieService.get(GUIDED_WORKFLOWS_OPEN_SUBCATEGORY_COOKIE_NAME) || '""';
        const subcategorySelectedId: number = JSON.parse(decodeURIComponent(subcategoryCookie));
        const unhashedLocation = window.location.toString().replace('/#/', '/');
        const newURL = new URL(unhashedLocation);
        const queryParams = new URLSearchParams(newURL.search);
        const guidedWorkFlow = queryParams.get('gw');
        const querySubcategoryId =
            guidedWorkFlow == null || Number.isNaN(guidedWorkFlow) ? null : Number(guidedWorkFlow);
        if (this.pagePath === undefined || this.pagePath === null) {
            this.pagePath = this.getPathName(window.location.pathname);
        }
        this.showWorkflows = hasGuidedWorkflowsFeature;
        // Only fetch guided workflows when the panel can actually be shown — the fetch is wasted for
        // anonymous users and users without the feature (including bots rendering the page shell).
        if (this.pagePath && this.showWorkflows) {
            this.guidedWorkflowsService.getPageGuidedWorkflows(this.pagePath).subscribe((categories) => {
                this.categories = categories;
                const allSubcategories: GuidedWorkflowSubcategory[] = categories.reduce(
                    (accumulator: GuidedWorkflowSubcategory[], category) => accumulator.concat(category.subcategories),
                    []
                );
                const selectedCategoryId = querySubcategoryId !== null ? querySubcategoryId : subcategorySelectedId;
                this.selectedSubcategoryId =
                    allSubcategories.find((subcategory) => subcategory.id === selectedCategoryId)?.id ?? null;
                if (this.selectedSubcategoryId !== null && !this.isSidePanelOpen()) {
                    this.togglePanel('workflows');
                }
            });
        }
        if (this.USER().authenticated && this.showNotifications) {
            this.fetchNotifications();
        }
    }

    ngOnDestroy(): void {
        this.backgroundJobEventsService.closeStream();
        if (this.notificationsFetchSubscription) {
            this.notificationsFetchSubscription.unsubscribe();
        }
        if (this.jobCompletedCheckTimeout !== null) {
            clearTimeout(this.jobCompletedCheckTimeout);
        }
    }

    private flashJobCompletedCheck(): void {
        this.showJobCompletedCheck.set(true);
        if (this.jobCompletedCheckTimeout !== null) {
            clearTimeout(this.jobCompletedCheckTimeout);
        }
        this.jobCompletedCheckTimeout = setTimeout(() => {
            this.jobCompletedCheckTimeout = null;
            this.showJobCompletedCheck.set(false);
        }, 5000);
    }

    setJobsInProgress(background_jobs: BackgroundJob[]): void {
        const jobs = background_jobs.map(({ children: _, ...job }) => job);
        const jobsMap = Object.fromEntries(jobs.map((job) => [job.job_id, job]));
        this.backgroundJobEventsService.jobsInProgress.set(jobsMap);
    }

    readonly hasRunningJobs = computed(() => Object.keys(this.backgroundJobEventsService.jobsInProgress()).length > 0);

    readonly hasRunningQuickExtractJob = computed(() =>
        Object.values(this.backgroundJobEventsService.jobsInProgress()).some(
            (job) => job.job_type === this.MODEL_PORTFOLIO_QUICK_EXTRACT_JOB_TYPE
        )
    );

    readonly runningJobs = computed(() => this.backgroundJobEventsService.jobsInProgress());

    toggleMenu(): void {
        this.isMenuOpen = !this.isMenuOpen;
    }

    handleAgentLink(): void {
        if (this.userHasAgentFeature) {
            this.togglePanel('ai_agent');
        } else if (this.USER().authenticated) {
            window.open(this.agentPageUrl(), '_blank');
        } else {
            window.location.href = this.agentPageUrl();
        }
    }

    togglePanel(type: SideMenuType): void {
        if (this.isSidePanelOpen() && this.type() === 'ai_agent') {
            this.saveDragState();
            if (this.isDetached()) {
                const drag = this.cdkDrag();
                if (drag) {
                    drag.reset();
                }
            }
        }

        if (this.isSidePanelOpen() && this.type() !== type) {
            // Panel is already open with a different type — just switch to the new type
            this.type.set(type);
        } else {
            // Panel is closed (open it) or same type clicked (close it)
            this.type.set(type);
            this.isSidePanelOpen.update((isOpen) => !isOpen);
            if (!this.isSidePanelOpen()) {
                this.clearGuidedWorkflowsSubcategoryCookie();
            }
        }

        // Make sure side panel top and height are set correctly after open
        if (this.isSidePanelOpen()) {
            afterNextRender(() => this.onScrollEvent(), { injector: this.injector });
        }

        if (this.type() === 'ai_agent') {
            this.syncAgentPanelPref();
        }
    }

    private syncAgentPanelPref(): void {
        // gate on prefs being loaded to fix race condition
        if (this.isOnAgentPage() || !this.prefsLoaded()) {
            return;
        }
        const isAgentVisible = this.isSidePanelOpen() && this.type() === 'ai_agent';
        this._agentUserPreferencesService
            .save({ agent_side_panel_open_on_load: isAgentVisible })
            .subscribe({ error: () => {} });
    }

    fetchNotifications(): void {
        if (this.notificationsFetchSubscription) {
            this.notificationsFetchSubscription.unsubscribe();
        }
        this.notificationsFetchSubscription = this.notificationsService.getNotifications().subscribe({
            next: (notifications: Notification[]) => {
                this.notifications.set(notifications);
            },
            error: () => {},
        });
    }

    deleteAllNotifications(): void {
        if (!this.notifications().length || !this.notificationsSideMenuComponent) {
            return;
        }
        this.notificationsSideMenuComponent.deleteAllNotifications();
    }

    closeMenu(): void {
        if (this.type() === 'ai_agent') {
            this.saveDragState();
        }
        this.isSidePanelOpen.set(false);
        this.clearGuidedWorkflowsSubcategoryCookie();

        if (this.type() === 'ai_agent') {
            this.syncAgentPanelPref();
        }
    }

    clearSteps(): void {
        this.workflowsMenuComponent.clearSteps();
    }

    private hasGuidedWorkflowQueryParam(): boolean {
        const unhashedLocation = window.location.toString().replace('/#/', '/');
        const gw = new URL(unhashedLocation).searchParams.get('gw');
        return gw !== null && !Number.isNaN(Number(gw));
    }

    getPathName(pathname: string): string {
        // Indicators don't use exchange symbols in URLs, but their name in snake case
        const indicatorsIdRegexSource = `(?!indicators(?:/|$))[a-z0-9_]+`;

        // We want to match only if after the ID there is either a slash or the end of the string.
        const securityIdRegex = pathname.includes('/indicators/')
            ? new RegExp(`/(?:${SECURITY_ID_REGEX_SOURCE}|${indicatorsIdRegexSource})(/|$)`)
            : new RegExp(`/(?:${SECURITY_ID_REGEX_SOURCE})(/|$)`);

        const decodeUriUntilStable = (str: string): string => {
            let lastDecodedStr = str;
            try {
                let currDecodedStr = decodeURIComponent(str);
                // Iterate until decoding no longer changes the string
                while (currDecodedStr !== lastDecodedStr) {
                    lastDecodedStr = currDecodedStr;
                    currDecodedStr = decodeURIComponent(lastDecodedStr);
                }
                return lastDecodedStr;
            } catch {
                // If decoding fails for any reason, just return the last successfully decoded string
                return lastDecodedStr;
            }
        };

        // Pathname may be double-url-encoded, so we decode until it doesn't change. If it wasn't double-url-encoded, or
        // even single-url-encoded, decoding multiple times won't hurt.
        const decodedPathname = decodeUriUntilStable(pathname);
        return decodedPathname.replace(securityIdRegex, '/0/').replace(/^\//, '').replace(/\/$/, '');
    }

    toggleDetach(): void {
        const reattaching = this._isPanelDetached();
        if (reattaching) {
            this.saveDragState();

            const drag = this.cdkDrag();
            if (drag) {
                drag.reset();
            }
        }
        this._isPanelDetached.update((x) => !x);
    }

    private saveDragState(): void {
        const pos = this.cdkDrag()?.getFreeDragPosition();
        if (pos) {
            this.savedDragPosition.set(pos);
        }
    }

    private clearGuidedWorkflowsSubcategoryCookie(): void {
        this.cookieService.delete(GUIDED_WORKFLOWS_OPEN_SUBCATEGORY_COOKIE_NAME, '/');
    }

    openBackgroundJobsProgressPopover(): void {
        this.accountsService.putUserPreferences({ always_display_jobs_progress_popover: true }).subscribe();
        this.backgroundJobsProgressPopover.openModal();
    }

    listenForRunningJobs(): void {
        this.backgroundJobService.fetchJobsInProgress().subscribe({
            next: (data: BackgroundJobsInProgressResponse) => {
                this.setJobsInProgress(data.background_jobs);
            },
            error: () => {},
        });
        if (!this.backgroundJobEventsService.isConnected()) {
            this.backgroundJobEventsService.stream();
        }
    }
}
